import clsx from "clsx";
import { useContext, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";

import {
  TypeScriptUtils,
  useFeatureFlags,
} from "@dndbeyond/character-components/es";
import {
  ApiAdapterUtils,
  ConfigUtils,
  rulesEngineSelectors,
  Constants,
  ApiException,
  CampaignUtils,
  characterEnvSelectors,
} from "@dndbeyond/character-rules-engine/es";
import { Dice, DiceNotifier } from "@dndbeyond/dice";
import { DiceToolbar } from "@dndbeyond/dice-components";
import { Platform } from "@dndbeyond/event-pipeline-lib/shared";
import D20 from "@dndbeyond/fontawesome-cache/svgs/light/dice-d20.svg";
import { GameLogContext } from "@dndbeyond/game-log-components";
import { getMessageBroker } from "@dndbeyond/message-broker-lib";
import {
  CustomDiceRoller,
  rollerImport,
} from "@dndbeyond/pocket-dimension-dice/components/CustomDiceRoller";
import "@dndbeyond/pocket-dimension-dice/components/CustomDiceRoller/index.css";
import { Context as DiceWorkerContext } from "@dndbeyond/pocket-dimension-dice/context";
import { instrumentDiceClear } from "@dndbeyond/pocket-dimension-dice/helpers/bi/instrumentDiceClear";
import { instrumentDiceContainerClose } from "@dndbeyond/pocket-dimension-dice/helpers/bi/instrumentDiceContainerClose";
import { instrumentDiceContainerOpen } from "@dndbeyond/pocket-dimension-dice/helpers/bi/instrumentDiceContainerOpen";
import { instrumentDiceRoll } from "@dndbeyond/pocket-dimension-dice/helpers/bi/instrumentDiceRoll";
import { useDiceSets } from "@dndbeyond/pocket-dimension-dice/hooks/useDiceSets";
import { useUserDiceDataStore } from "@dndbeyond/pocket-dimension-dice/hooks/useUserDiceDataStore";
import { AnchoredPopover } from "@dndbeyond/ttui/components/AnchoredPopover";

import { Button } from "~/components/Button";
import { useAuth } from "~/contexts/Authentication";
import { useCharacterEngine } from "~/hooks/useCharacterEngine";
import { useDispatch } from "~/hooks/useDispatch";

import { appEnvActions } from "../../actions/appEnv";
import {
  appEnvSelectors,
  characterRollContextSelectors,
} from "../../selectors";
import { AppLoggerUtils } from "../../utils";
import styles from "./DiceContainer.module.css";

// <canvas id="dice-rolling-canvas" className={'dice-rolling-panel__container'} />
// this is the canvas element that we will use to draw the dice
// TODO: this is a temporary solution, we need to find a better way to do this
// waiting for adventure team to provide a better solution
const canvasElement = document.createElement("canvas");
canvasElement.id = "dice-rolling-canvas";
canvasElement.classList.add("dice-rolling-panel__container");
const location = "characterSheet";

export default function DiceContainer({ canShow = true }) {
  const { env } = useContext(DiceWorkerContext);
  const dispatch = useDispatch();
  const theme = useSelector(rulesEngineSelectors.getCharacterTheme);
  const diceEnabled = useSelector(appEnvSelectors.getDiceEnabled);
  const diceFeatureConfiguration = useSelector(
    appEnvSelectors.getDiceFeatureConfiguration
  );
  const characterRollContext = useSelector(
    characterRollContextSelectors.getCharacterRollContext
  );
  const campaign = useSelector(rulesEngineSelectors.getCampaign);
  const userConfigApi = "/diceuserconfig/v1/get";
  const user = useAuth();
  const [{ messageTargetOptions, defaultMessageTargetOption, entityId }] =
    useContext(GameLogContext);
  const canvasContainer = useRef<HTMLDivElement | null>(null);
  const { tacticalMapsShared3dDiceFlag } = useFeatureFlags();
  const [isCustomDiceRollerOpen, setIsCustomDiceRollerOpen] = useState(false);
  const diceSets = useDiceSets();
  const setId = useUserDiceDataStore(
    (state) => state.getUserDiceData(Number(user?.id))?.setId
  );
  const { characterName } = useCharacterEngine();

  const isSheet =
    useSelector(characterEnvSelectors.getContext) ===
    Constants.AppContextTypeEnum.SHEET;

  useEffect(() => {
    if (!canvasContainer.current?.contains(canvasElement)) {
      canvasContainer.current?.appendChild(canvasElement);
    }
  });

  useEffect(() => {
    getMessageBroker().then((mb) => {
      if (mb && user?.id) {
        if (campaign) {
          mb.gameId = CampaignUtils.getId(campaign).toString();
        } else {
          mb.gameId = undefined;
        }

        mb.userId = user.id;
      }
      ConfigUtils.configureRulesEngine({ messageBroker: mb });
      if (diceFeatureConfiguration.enabled) {
        const rulesEngineConfig = ConfigUtils.getCurrentRulesEngineConfig();
        if (rulesEngineConfig.apiAdapter) {
          rulesEngineConfig.apiAdapter
            .get(`${diceFeatureConfiguration.apiEndpoint}${userConfigApi}`)
            .then((x) => {
              const diceCanvas = canvasElement;
              Dice.init({
                isDebug: false,
                assetBaseLocation: diceFeatureConfiguration.assetBaseLocation,
                trackingId: diceFeatureConfiguration.trackingId,
                sessionName: "Dice",
                notifier: new DiceNotifier(),
                canvas: diceCanvas || undefined,
                previewMode: false,
                diceUserConfig: ApiAdapterUtils.getResponseData(x) as any,
                canUseShared3dDice: tacticalMapsShared3dDiceFlag,
              })
                .then(() =>
                  document.addEventListener("click", () => Dice.clear())
                )
                .catch((error: Error) => {
                  AppLoggerUtils.logMessage(
                    error.message,
                    Constants.LogMessageType.WARNING
                  );
                  dispatch(
                    appEnvActions.dataSet({
                      diceEnabled: false,
                    })
                  );
                });
            })
            .catch((error: ApiException) => {
              AppLoggerUtils.logMessage(
                error.message,
                Constants.LogMessageType.WARNING
              );
              dispatch(
                appEnvActions.dataSet({
                  diceEnabled: false,
                })
              );
            });
        }
      } else {
        Dice.analyticsInit(diceFeatureConfiguration.trackingId, "Dice");
      }
    });
  }, [
    campaign,
    diceFeatureConfiguration.apiEndpoint,
    diceFeatureConfiguration.assetBaseLocation,
    diceFeatureConfiguration.enabled,
    diceFeatureConfiguration.trackingId,
    dispatch,
    isSheet,
    tacticalMapsShared3dDiceFlag,
    user?.id,
  ]);

  return ReactDOM.createPortal(
    <div className={"dice-rolling-panel"} ref={canvasContainer}>
      {canShow && diceEnabled && !tacticalMapsShared3dDiceFlag && (
        <DiceToolbar
          rollContext={characterRollContext}
          rollTargetOptions={
            messageTargetOptions && isSheet
              ? Object.values(messageTargetOptions?.entities).filter(
                  TypeScriptUtils.isNotNullOrUndefined
                )
              : undefined
          }
          rollTargetDefault={defaultMessageTargetOption}
          themeColor={theme.themeColor}
          userId={parseInt(user?.id || "0", 10)}
          canUseShared3dDice={tacticalMapsShared3dDiceFlag}
        />
      )}
      {canShow && diceEnabled && tacticalMapsShared3dDiceFlag && (
        <AnchoredPopover
          offset={16}
          open={isCustomDiceRollerOpen}
          allowedPlacements={["top-start"]}
          referenceComponent={
            <Button
              className={clsx([
                styles.button,
                isCustomDiceRollerOpen && styles.customDiceRollOpen,
              ])}
              onClick={() => {
                setIsCustomDiceRollerOpen(!isCustomDiceRollerOpen);

                // isCustomDiceRollerOpen is the previous state here
                if (isCustomDiceRollerOpen) {
                  instrumentDiceContainerClose(
                    user,
                    Platform.Web,
                    location,
                    campaign?.id,
                    undefined,
                    env
                  );
                } else {
                  instrumentDiceContainerOpen(
                    user,
                    Platform.Web,
                    location,
                    campaign?.id,
                    undefined,
                    env
                  );
                }
              }}
              themed
              onMouseEnter={rollerImport}
            >
              <D20 />
            </Button>
          }
          floatingArrow
          floatingArrowClassName={styles.floatingArrow}
        >
          {diceSets && (
            <CustomDiceRoller
              rollCallback={() => setIsCustomDiceRollerOpen(false)}
              onClose={() => setIsCustomDiceRollerOpen(false)}
              diceSets={diceSets}
              gameId={campaign?.id}
              canUseShared3dDice
              visible={isCustomDiceRollerOpen}
              setId={setId}
              entityType="character"
              entityId={entityId}
              messageTarget={campaign?.id}
              name={characterName ?? undefined}
              rollInstrumentationCallback={(eventPayload) =>
                instrumentDiceRoll(
                  user,
                  eventPayload,
                  Platform.Web,
                  location,
                  campaign?.id,
                  undefined,
                  env
                )
              }
              clearDiceCallback={() =>
                instrumentDiceClear(
                  user,
                  Platform.Web,
                  location,
                  campaign?.id,
                  undefined,
                  env
                )
              }
            />
          )}
        </AnchoredPopover>
      )}
      {/* We want this one so it is sticking around, 
            also this is where the canvas goes when we append */}
      {/* <canvas className={'dice-rolling-panel__container'} ref={canvas} /> */}
    </div>,
    document.body
  );
}
